import * as React from 'react';

import { BadgeModel } from 'models';

import {
  UitkBadge,
  UitkBadgeTheme,
  UitkDealGenericBadge,
  UitkDealMemberBadge,
  UitkFamilyFriendlyBadge,
  UitkSavedBadge,
  UitkSponsoredBadge,
  UitkSupplierPromoBadge,
  UitkViewedBadge,
  UitkVipBadge,
} from 'uitk-react-badge';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkMark, UitkMarkSize } from 'uitk-react-mark';

import { Theme } from '__generated__/api';

interface BadgeGeneratorProps {
  badge: BadgeModel;
  badgeClasses?: string;
  uniqueId?: number;
}

export const BadgeGenerator: React.FunctionComponent<BadgeGeneratorProps> = ({ badge, badgeClasses, uniqueId }) => {
  if (!badge.icon && !badge.mark && !badge.text) {
    return null;
  }
  const DecorativeElement = getDecorativeElement(badge, uniqueId);

  switch (badge.theme) {
    case Theme.SPONSORED:
      return <UitkSponsoredBadge className={badgeClasses} text={badge.text} />;
    case Theme.VIP_ACCESS:
      return <UitkVipBadge className={badgeClasses} text={badge.text} />;
    case Theme.DEAL_ADD_ON:
      return <UitkDealGenericBadge children={DecorativeElement} className={badgeClasses} text={badge.text} />;
    case Theme.DEAL_BUNDLED:
      return <UitkDealGenericBadge children={DecorativeElement} className={badgeClasses} text={badge.text} />;
    case Theme.DEAL_GENERIC:
      return <UitkDealGenericBadge children={DecorativeElement} className={badgeClasses} text={badge.text} />;
    case Theme.DEAL_MEMBER:
      return <UitkDealMemberBadge children={DecorativeElement} className={badgeClasses} text={badge.text} />;
    case Theme.FAMILY_FRIENDLY:
      return <UitkFamilyFriendlyBadge children={DecorativeElement} className={badgeClasses} text={badge.text} />;
    case Theme.SAVED:
      return <UitkSavedBadge children={DecorativeElement} className={badgeClasses} text={badge.text} theme={UitkBadgeTheme[badge.theme]} />;
    case Theme.SUPPLIER_PROMO:
      return (
        <UitkSupplierPromoBadge
          children={DecorativeElement}
          className={badgeClasses}
          text={badge.text}
          theme={UitkBadgeTheme[badge.theme]}
        />
      );
    case Theme.VIEWED:
      return <UitkViewedBadge children={DecorativeElement} className={badgeClasses} text={badge.text} />;
    default:
      return (
        <UitkBadge className={badgeClasses} text={badge.text} theme={UitkBadgeTheme[badge.theme]}>
          {badge?.icon && <UitkIcon name={badge.icon} />}
        </UitkBadge>
      );
  }
};

const getDecorativeElement = (badge: BadgeModel, uniqueId?: number): JSX.Element | null => {
  const icon = badge?.icon ? (
    <UitkIcon
      className="uitk-badge-icon"
      name={badge.icon}
      size={UitkIconSize.EXTRA_SMALL}
      title={badge.iconTitle}
      id={badge.icon + uniqueId}
    />
  ) : null;
  const mark = badge?.mark ? (
    <UitkMark className="uitk-badge-mark" name={badge.mark} size={UitkMarkSize.EXTRA_SMALL} description={badge?.text} />
  ) : null;

  return icon ? icon : mark;
};

BadgeGenerator.displayName = 'BadgeGenerator';
